import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { SITE_CONTACT } from "@/data/site";
import PaymentLogos from "@/components/PaymentLogos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  {
    heading: "Experiences",
    links: [
      ["Ayurveda", "/wellness"], ["Yoga", "/experiences"], ["Beaches", "/experiences"],
      ["Houseboats", "/experiences"], ["Mountains", "/experiences"], ["Traditional Living", "/experiences"],
    ],
  },
  {
    heading: "Discover",
    links: [
      ["Destinations", "/destinations"], ["Wellness & Ayurveda", "/wellness"],
      ["Sample Itineraries", "/itineraries"], ["The Travel Bug Way", "/partnership"],
    ],
  },
  {
    heading: "Company",
    links: [
      ["About", "/about"], ["Experiences", "/experiences"],
      ["Travel Info", "/travel-info"], ["Contact", "/contact"],
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Thank you — you're on the list.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="grain-dark bg-forest text-ivory relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-20 pb-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-ivory/15">
          <div className="lg:col-span-5">
            <h3 className="font-serif text-4xl md:text-5xl leading-none tracking-tight">Travel Bug India</h3>
            <p className="mt-3 overline text-gold">Discover India</p>
            <p className="mt-2 text-sm text-ivory/60 tracking-wide">Authentic Journeys · Timeless Experiences</p>
            <p className="mt-6 max-w-md text-ivory/70 font-light leading-relaxed">
              Curated India journeys and warm local hosts — authentic, unhurried
              experiences, handcrafted for the curious traveller.
            </p>
            <address className="mt-6 not-italic space-y-2 text-sm text-ivory/70 min-w-0">
              <p className="flex items-start gap-2 break-words"><MapPin size={15} className="text-gold shrink-0 mt-0.5" /><span>{SITE_CONTACT.address}</span></p>
              <a href={`mailto:${SITE_CONTACT.email}`} className="flex items-start gap-2 hover:text-gold break-all"><Mail size={15} className="text-gold shrink-0 mt-0.5" /><span>{SITE_CONTACT.email}</span></a>
              <a href={`tel:${SITE_CONTACT.phoneHref}`} className="flex items-start gap-2 hover:text-gold"><Phone size={15} className="text-gold shrink-0 mt-0.5" /><span>{SITE_CONTACT.phone}</span></a>
            </address>
            <form onSubmit={subscribe} className="mt-8 flex max-w-sm" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 bg-ivory/10 border border-ivory/20 rounded-l-full px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 outline-none focus:border-gold"
              />
              <button
                data-testid="newsletter-submit"
                disabled={loading}
                className="rounded-r-full bg-gold text-forest px-5 py-3 disabled:opacity-60"
                aria-label="Subscribe"
              >
                <ArrowRight size={18} />
              </button>
            </form>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10 sm:gap-10">
            {COLS.map((c) => (
              <div key={c.heading}>
                <h4 className="overline text-ivory/50 mb-5">{c.heading}</h4>
                <ul className="space-y-3">
                  {c.links.map(([label, to]) => (
                    <li key={label}>
                      <Link to={to} className="text-sm text-ivory/75 hover:text-gold transition-colors">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="py-10 border-b border-ivory/15 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-4 flex items-start gap-3">
            <ShieldCheck size={25} className="text-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-base font-semibold">Secure Online Payments</p>
              <p className="mt-1 text-xs text-ivory/55 leading-relaxed">Clear invoices, verified instructions and booking terms.</p>
            </div>
          </div>
          <div className="lg:col-span-8 max-w-2xl w-full">
            <PaymentLogos dark />
          </div>
        </div>

        <div className="pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-ivory/50 text-xs tracking-wide">
            © {new Date().getFullYear()} Travel Bug India. Authentic Journeys · Timeless Experiences.
          </p>
          <Link
            to="/contact"
            data-testid="footer-partner-cta"
            className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full border border-gold text-gold px-7 py-3 text-sm font-semibold tracking-wide hover:bg-gold hover:text-forest transition-colors"
          >
            Plan Your Journey <ArrowRight size={16} />
          </Link>
        </div>
        <p className="mt-6 text-[0.65rem] text-ivory/35">Landmark photography: Dinesh Kumar Dholiya, Rijin S and Wikimedia Commons contributors (CC BY-SA).</p>
      </div>
    </footer>
  );
}
